import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Icon } from './JournalOverview';
import Brand from './Brand';
import { signedNumber, tradeHero, tradeR } from './share-utils';
import './trade-result.css';

const DISPLAY_TIME = 7000;

function OutcomeMark({ outcome }) {
  return <div className="tl-outcome-art" aria-hidden="true">
    <div className="tl-outcome-halo"/>
    <div className="tl-outcome-orbit"><i/><i/></div>
    <svg viewBox="0 0 120 120" className="tl-outcome-mark">
      <circle className="tl-outcome-track" cx="60" cy="60" r="43"/>
      <circle className="tl-outcome-ring" cx="60" cy="60" r="43" pathLength="1"/>
      <path className="tl-outcome-ticks" d="M60 4v5M60 111v5M4 60h5M111 60h5M20 20l4 4M96 96l4 4M20 100l4-4M96 24l4-4"/>
      <path className="tl-outcome-market-line" pathLength="1" d={outcome==='win'?'M8 91L25 87L36 94L51 76L64 79L78 56L92 61L113 33':outcome==='loss'?'M8 35L25 44L36 38L51 62L64 58L78 78L92 69L113 84':'M8 63L25 57L36 65L51 55L64 61L78 54L92 60L113 57'}/>
      {outcome === 'win' ? <>
        <path className="tl-outcome-stroke" pathLength="1" d="m40 61 13 13 28-29"/>
        <path className="tl-outcome-accent" d="M92 20v12M86 26h12"/>
      </> : outcome === 'loss' ? <>
        <path className="tl-outcome-stroke" pathLength="1" d="M60 46c-7-6-15-7-25-4v32c10-3 18-2 25 4 7-6 15-7 25-4V42c-10-3-18-2-25 4v32"/>
        <path className="tl-outcome-accent" d="M42 51h9M42 59h9M69 51h9M69 59h9"/>
      </> : <path className="tl-outcome-stroke" pathLength="1" d="M43 54h34M43 66h34"/>}
    </svg>
  </div>;
}

export default function TradeResultMoment({ trade, onDone, onAddDetails }) {
  const done = useRef(onDone), completed = useRef(false), remaining = useRef(DISPLAY_TIME);
  const [hovered, setHovered] = useState(false), [focused, setFocused] = useState(false);
  const [hidden, setHidden] = useState(() => document.hidden);
  done.current = onDone;
  const finish = useCallback(action => {
    if (completed.current) return;
    completed.current = true;
    action();
  }, []);
  const dismiss = useCallback(() => finish(() => done.current()), [finish]);
  const paused = hovered || focused || hidden;
  useEffect(() => {
    const visibility = () => setHidden(document.hidden);
    const key = event => {
      // An open settings/menu dialog owns Escape without dismissing this notice too.
      if (event.key === 'Escape' && !event.defaultPrevented && !document.querySelector('[aria-modal="true"]')) dismiss();
    };
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('keydown', key);
    };
  }, [dismiss]);
  useEffect(() => {
    if (paused || completed.current) return;
    const started = Date.now();
    const timer = setTimeout(dismiss, remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - started));
    };
  }, [paused, dismiss]);
  const outcome = trade.result === 'WIN' ? 'win' : trade.result === 'LOSS' ? 'loss' : 'neutral';
  const win = outcome === 'win', hero = tradeHero(trade), multiple = tradeR(trade);
  return createPortal(<motion.div className="tl-result-stage" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.16 }}>
    <div className={`tl-result-atmosphere is-${outcome}`} aria-hidden="true"><i/><i/><i/></div>
    <section className={`tl-result-moment is-${outcome}`} aria-label="Trade saved"
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false); }}>
      <header><Brand size={18}/><span className="tl-result-saved"><Icon name="check" size={12}/>Journal saved</span><button className="tl-icon-button" aria-label="Close trade result" onClick={dismiss}><Icon name="close" size={17}/></button></header>
      <OutcomeMark outcome={outcome}/>
      <div className="tl-result-copy" role="status" aria-atomic="true">
        <span className="tl-result-label">{win ? 'WIN RECORDED' : outcome === 'loss' ? 'LOSS RECORDED' : 'BREAK EVEN RECORDED'}</span>
        <h2>{win ? 'A moment of progress.' : outcome === 'loss' ? 'A lesson to take forward.' : 'Your discipline, recorded.'}</h2>
        <div className="tl-result-value">{hero.value}<small>{hero.unit}</small></div>
        <div className="tl-result-trade"><strong>{trade.instrument}</strong>{trade.direction && <span>{trade.direction}</span>}{multiple != null && <span>{signedNumber(multiple)}R</span>}</div>
        <p>{win ? 'Keep what worked. Add the details you’ll want to repeat.' : outcome === 'loss' ? 'Keep the context. Your next decision starts with what you learn.' : 'Keep the reasoning beside the result.'}</p>
      </div>
      <footer><button className="tl-btn" onClick={dismiss}>Done</button><button className="tl-btn tl-btn-primary" onClick={() => finish(onAddDetails)}>{win ? 'Capture the details' : 'Reflect on this trade'}<Icon name="arrow" size={15}/></button></footer>
      <div className="tl-result-timer" style={{ animationPlayState: paused ? 'paused' : 'running' }} aria-hidden="true"/>
    </section>
  </motion.div>, document.body);
}
